import * as React from 'react';

import { Button } from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { DriverForm } from './DriverForm';

import { useTranslations } from '@/transportation/i18n/useTranslations';
import { useCreateDriverMutation } from '~/modules/transportation/hooks/mutations/useCreateDriverMutation';
import { useUpdateDriverMutation } from '~/modules/transportation/hooks/mutations/useUpdateDriverMutation';
import {
  $driverForm,
  $resetDriverForm,
  $updateDriverForm,
} from '~/modules/transportation/state/driverForm.localState';
import { DriverRecord } from '~/modules/transportation/types';

interface DriverDialogProps {
  driver?: Omit<DriverRecord, 'user'> & { userId?: string };
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DriverDialog = ({
  driver,
  isOpen,
  onClose,
  onSuccess,
}: DriverDialogProps) => {
  const toast = useToast();
  const t = useTranslations();
  const { mutateAsync: createDriver, isPending: isCreating } =
    useCreateDriverMutation();
  const { mutateAsync: updateDriver, isPending: isUpdating } =
    useUpdateDriverMutation();
  const formData = useAtomValue($driverForm);
  const resetForm = useSetAtom($resetDriverForm);
  const updateForm = useSetAtom($updateDriverForm);

  React.useEffect(() => {
    if (isOpen && driver) {
      updateForm(driver);
    }
  }, [isOpen, driver, updateForm]);

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const handleSubmit = async () => {
    try {
      if (driver) {
        await updateDriver({
          id: driver.id,
          ...formData,
        });
        toast.success(t.driversView.messages.updateSuccess);
      } else {
        await createDriver(formData);
        toast.success(t.driversView.messages.createSuccess);
      }
      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.driversView.messages.error,
      );
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@DriverDialog"
    >
      <DialogContent slot="title" position="center">
        <DialogHeader>
          <div className="text-xl font-semibold">
            {driver
              ? t.driversView.dialog.title.edit
              : t.driversView.dialog.title.create}
          </div>
        </DialogHeader>

        <div className="p-4">
          <DriverForm isEditing={!!driver} />
        </div>

        <DialogFooter>
          <Button
            color="failure"
            variant="outline"
            isPending={isCreating || isUpdating}
            onPress={handleClose}
          >
            {t.driversView.dialog.actions.cancel}
          </Button>
          <Button
            color="primary"
            variant="solid"
            isPending={isCreating || isUpdating}
            isLoading={isCreating || isUpdating}
            onPress={handleSubmit}
          >
            {driver
              ? t.driversView.dialog.actions.save
              : t.driversView.dialog.actions.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
